import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { FormPageLayout } from '@/components/layout/form-page-layout';
import {
        Alert,
        AlertDescription,
        AlertTitle
} from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useRouteParams } from '@/contexts/RouteContext';
import { useEditPageHeader } from '@/hooks/useEditPageHeader';
import { useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import {
        getMutationOptions,
        showMutationErrorToast,
        showMutationSuccessToast
} from '@/lib/mutationRefetch';

import {
        CustomerForm,
        type CustomerFormValues,
        type SelectOption
} from './customer-form';
import { buildUpdateCustomerInput } from './customer-input';
import {
        CUSTOMER_FORM_DATA_QUERY,
        CUSTOMER_QUERY,
        CUSTOMER_UPDATE_MUTATION
} from './customers.gql.tsx';

interface CustomerFormDataQuery {
        customerCategories: Array<{
                customerCategoryId: string;
                description?: string | null;
        }>;
        contactTypes: Array<{
                contactTypeId: number;
                name?: string | null;
                isActive: boolean;
        }>;
        addressTypes: Array<{
                addressTypeId?: number | null;
                name?: string | null;
        }>;
        vehicleTypes: Array<{
                vehicleTypeId: number;
                name?: string | null;
                isActive: boolean;
        }>;
}

interface CustomerQueryContact {
        customerContactId: string;
        contactTypeId?: number | null;
        contactValue?: string | null;
        isPrimary: boolean;
        notes?: string | null;
        isDeleted?: boolean;
}

interface CustomerQueryAddress {
        customerAddressId: string;
        addressId: string;
        addressTypeId?: number | null;
        address?: {
                streetAddress?: string | null;
                city?: string | null;
                state?: string | null;
                postalCode?: string | null;
                country?: string | null;
        } | null;
}

interface CustomerQueryBankDetail {
        bankDetailsId: string;
        bankName?: string | null;
        accountNumber?: string | null;
        iban?: string | null;
        bankCode?: string | null;
        accountHolderName?: string | null;
        isPrimary: boolean;
        isDeleted: boolean;
}

interface CustomerQueryBeneficiary {
        customerBeneficiaryId: string;
        beneficiaryId: string;
        isActive: boolean;
        notes?: string | null;
        relationshipType?: string | null;
        isDeleted: boolean;
        beneficiary?: {
                customerId: string;
                firmName?: string | null;
                firstName?: string | null;
                lastName?: string | null;
                isCompany: boolean;
        } | null;
}

interface CustomerQueryVehicle {
        vehicleId: string;
        plateNumber?: string | null;
        vehicleTypeId?: number | null;
        make?: string | null;
        model?: string | null;
        color?: string | null;
        isActive: boolean;
        vehicleType?: {
                vehicleTypeId: number;
                name?: string | null;
        } | null;
}

interface CustomerQueryResponse {
        customer?: {
                customerId: string;
                isCompany: boolean;
                firmName?: string | null;
                firstName?: string | null;
                lastName?: string | null;
                email?: string | null;
                fiscalNumber?: string | null;
                taxOffice?: string | null;
                billingLegalName?: string | null;
                billingTradeName?: string | null;
                billingCountryCode?: string | null;
                marketingOptIn: boolean;
                notes?: string | null;
                isActive: boolean;
                customerCategoryId: string;
                preferredLanguage?: string | null;
                companyRegistrationNumber?: string | null;
                industry?: string | null;
                companySize?: string | null;
                customerContacts?: CustomerQueryContact[] | null;
                customerAddresses?: CustomerQueryAddress[] | null;
                bankDetails?: CustomerQueryBankDetail[] | null;
                customerBeneficiaries?: CustomerQueryBeneficiary[] | null;
                vehicles?: CustomerQueryVehicle[] | null;
        } | null;
}

const ULID_REGEX = /^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{26}$/u;

const formatDisplayName = (record: NonNullable<CustomerQueryResponse['customer']>) => {
        if (record.isCompany) {
                return record.firmName?.trim() || 'Business customer';
        }

        const parts = [record.firstName, record.lastName]
                .map(part => part?.trim())
                .filter((part): part is string => Boolean(part));

        if (parts.length > 0) {
                return parts.join(' ');
        }

        return record.email?.trim() || 'Unnamed customer';
};


export const CustomerEdit = () => {
const { id } = useRouteParams();
	const go = useGo();
	useEditPageHeader({ id, resourceName: 'customers' });

	const form = useForm<CustomerFormValues>({
                defaultValues: {
                        isCompany: false,
                        firmName: '',
                        firstName: '',
                        lastName: '',
                        email: '',
                        fiscalNumber: '',
                        taxOffice: '',
                        billingLegalName: '',
                        billingTradeName: '',
                        billingCountryCode: '',
                        marketingOptIn: false,
                        notes: '',
                        isActive: true,
                        customerCategoryId: '',
                        preferredLanguage: '',
                        companyRegistrationNumber: '',
                        industry: '',
                        companySize: '',
                        contacts: [],
                        addresses: [],
                        bankDetails: [],
                        beneficiaries: []
                }
        });

        const isValidUlid = useMemo(() => {
                return typeof id === 'string' && ULID_REGEX.test(id.toUpperCase());
        }, [id]);

        const {
                data: lookupData,
                loading: lookupsLoading,
                error: lookupsError
        } = useQuery<CustomerFormDataQuery>(CUSTOMER_FORM_DATA_QUERY);

        const {
                data: customerResponse,
                loading: customerLoading,
                error: customerError
        } = useQuery<CustomerQueryResponse>(CUSTOMER_QUERY, {
                variables: { id },
                skip: !id || !isValidUlid
        });

        const customerRecord = customerResponse?.customer ?? null;

        const [updateCustomer, { loading: isSubmitting }] = useMutation(
                CUSTOMER_UPDATE_MUTATION
        );

        const customerCategoryOptions = useMemo<SelectOption[]>(() => {
                if (!lookupData?.customerCategories) {
                        return [];
                }

                return lookupData.customerCategories.map(category => ({
                        value: category.customerCategoryId,
                        label: category.description ?? 'Customer category'
                }));
        }, [lookupData?.customerCategories]);

        const contactTypeOptions = useMemo<SelectOption[]>(() => {
                if (!lookupData?.contactTypes) {
                        return [];
                }

                return lookupData.contactTypes
                        .filter(contactType => contactType.isActive)
                        .map(contactType => ({
                                value: String(contactType.contactTypeId),
                                label: contactType.name ?? `Type ${contactType.contactTypeId}`
                        }));
        }, [lookupData?.contactTypes]);

        const addressTypeOptions = useMemo<SelectOption[]>(() => {
                if (!lookupData?.addressTypes) {
                        return [];
                }

                return lookupData.addressTypes
                        .filter(addressType => addressType.addressTypeId != null)
                        .map(addressType => ({
                                value: String(addressType.addressTypeId),
                                label: addressType.name ?? `Type ${addressType.addressTypeId}`
                        }));
        }, [lookupData?.addressTypes]);

        const vehicleTypeOptions = useMemo<SelectOption[]>(() => {
                if (!lookupData?.vehicleTypes) {
                        return [];
                }

                return lookupData.vehicleTypes.map(vehicleType => ({
                        value: String(vehicleType.vehicleTypeId),
                        label: vehicleType.name ?? `Type ${vehicleType.vehicleTypeId}`
                }));
        }, [lookupData?.vehicleTypes]);

        useEffect(() => {
                if (!customerRecord) {
                        return;
                }

                const contactValues =
                        customerRecord.customerContacts?.filter(contact => !contact?.isDeleted) ?? [];
                const addressValues = customerRecord.customerAddresses ?? [];
				const bankDetailValues =
						customerRecord.bankDetails?.filter(detail => !detail.isDeleted) ?? [];
				const beneficiaryValues =
						customerRecord.customerBeneficiaries?.filter(beneficiary => !beneficiary.isDeleted) ?? [];
				const vehicleValues = customerRecord.vehicles ?? [];

				// Helper function to get beneficiary display name
				const getBeneficiaryDisplayName = (beneficiary: CustomerQueryBeneficiary) => {
					if (!beneficiary.beneficiary) return '';
					const ben = beneficiary.beneficiary;
					if (ben.isCompany && ben.firmName) {
						return ben.firmName;
					}
					if (ben.firstName || ben.lastName) {
						return [ben.firstName, ben.lastName].filter(Boolean).join(' ');
					}
					return ben.customerId;
				};
                form.reset({
                        isCompany: customerRecord.isCompany,
                        firmName: customerRecord.firmName ?? '',
                        firstName: customerRecord.firstName ?? '',
                        lastName: customerRecord.lastName ?? '',
                        email: customerRecord.email ?? '',
                        fiscalNumber: customerRecord.fiscalNumber ?? '',
                        taxOffice: customerRecord.taxOffice ?? '',
                        billingLegalName: customerRecord.billingLegalName ?? '',
                        billingTradeName: customerRecord.billingTradeName ?? '',
                        billingCountryCode: customerRecord.billingCountryCode ?? '',
                        marketingOptIn: customerRecord.marketingOptIn,
                        notes: customerRecord.notes ?? '',
                        isActive: customerRecord.isActive,
                        customerCategoryId: customerRecord.customerCategoryId,
                        preferredLanguage: customerRecord.preferredLanguage ?? '',
                        companyRegistrationNumber:
                                customerRecord.companyRegistrationNumber ?? '',
                        industry: customerRecord.industry ?? '',
                        companySize: customerRecord.companySize ?? '',
                        contacts: contactValues.map(contact => ({
                                customerContactId: contact.customerContactId,
                                contactTypeId:
                                        contact.contactTypeId != null
                                                ? String(contact.contactTypeId)
                                                : '',
                                contactValue: contact.contactValue ?? '',
                                isPrimary: contact.isPrimary,
                                notes: contact.notes ?? ''
                        })),
                        addresses: addressValues.map(address => ({
                                customerAddressId: address.customerAddressId,
                                addressId: address.addressId,
                                addressTypeId:
                                        address.addressTypeId != null
                                                ? String(address.addressTypeId)
                                                : '',
                                streetAddress: address.address?.streetAddress ?? '',
                                city: address.address?.city ?? '',
                                state: address.address?.state ?? '',
                                postalCode: address.address?.postalCode ?? '',
                                country: address.address?.country ?? ''
                        })),
                        bankDetails: bankDetailValues.map(detail => ({
                                bankDetailsId: detail.bankDetailsId,
                                bankName: detail.bankName ?? '',
                                accountNumber: detail.accountNumber ?? '',
                                iban: detail.iban ?? '',
                                bankCode: detail.bankCode ?? '',
                                accountHolderName: detail.accountHolderName ?? '',
                                isPrimary: detail.isPrimary
                        })),
                        beneficiaries: beneficiaryValues.map(beneficiary => ({
                                customerBeneficiaryId: beneficiary.customerBeneficiaryId,
                                beneficiaryCustomerId: beneficiary.beneficiaryId,
                                beneficiaryDisplayName: getBeneficiaryDisplayName(beneficiary),
                                isActive: beneficiary.isActive,
                                notes: beneficiary.notes ?? '',
                                relationshipType: beneficiary.relationshipType ?? ''
                        })),
                        vehicles: vehicleValues.map(vehicle => ({
                                vehicleId: vehicle.vehicleId,
                                plateNumber: vehicle.plateNumber ?? '',
                                vehicleTypeId:
                                        vehicle.vehicleTypeId != null
                                                ? String(vehicle.vehicleTypeId)
                                                : '',
                                make: vehicle.make ?? '',
                                model: vehicle.model ?? '',
                                color: vehicle.color ?? '',
                                isActive: vehicle.isActive
                        }))
                });
        }, [customerRecord, form]);

        const handleSubmit = async (values: CustomerFormValues) => {
                if (!customerRecord?.customerId) {
                        showMutationErrorToast('customers', 'update', { message: 'Customer identifier is missing.' });
                        return;
                }

                const payload = buildUpdateCustomerInput(values, customerRecord.customerId);

                try {
                        const { data } = await updateCustomer(
                                getMutationOptions('customers', { input: payload })
                        );

                        const message = extractCheckResultError(data?.updateCustomer);

                        if (message) {
                                showMutationErrorToast('customers', 'update', { message });
                                return;
                        }

                        showMutationSuccessToast('customers', 'update');

                        // Navigate back with the edited customer ID for selection
                        go({
                                to: '/customers',
                                state: { selectCustomerId: customerRecord.customerId }
                        });
                } catch (error) {
                        showMutationErrorToast('customers', 'update', { error });
                }
        };

        if (!id) {
                return (
                        <FormPageLayout>
                                <Alert variant="destructive">
                                        <AlertTitle>Invalid route</AlertTitle>
                                        <AlertDescription>
                                                The customer identifier is missing from the URL.
                                        </AlertDescription>
                                </Alert>
                                <div className="mt-4">
                                        <Button variant="outline" onClick={() => go({ to: '/customers' })}>
                                                Back to customers
                                        </Button>
                                </div>
                        </FormPageLayout>
                );
        }

        if (!isValidUlid) {
                return (
                        <FormPageLayout>
                                <Card className="space-y-3">
                                        <h2 className="text-lg font-semibold text-destructive">
                                                Invalid customer identifier
                                        </h2>
                                        <p className="text-sm text-muted-foreground">
                                                The provided customer id{' '}
                                                <code className="px-2 py-1 rounded bg-muted">{id}</code>{' '}
                                                is not a valid ULID value.
                                        </p>
                                        <Button variant="outline" onClick={() => go({ to: '/customers' })}>
                                                Back to customers
                                        </Button>
                                </Card>
                        </FormPageLayout>
                );
        }

        if (customerLoading && !customerRecord) {
                return (
                        <FormPageLayout>
                                <Card className="space-y-2">
                                        <h2 className="text-lg font-semibold">Loading customer</h2>
                                        <p className="text-sm text-muted-foreground">
                                                Retrieving customer information...
                                        </p>
                                </Card>
                        </FormPageLayout>
                );
        }

        if (customerError) {
                return (
                        <FormPageLayout>
                                <Alert variant="destructive">
                                        <AlertTitle>Unable to load customer</AlertTitle>
                                        <AlertDescription>
                                                {customerError.message ?? 'Failed to load customer details.'}
                                        </AlertDescription>
                                </Alert>
                                <div className="mt-4">
                                        <Button variant="outline" onClick={() => go({ to: '/customers' })}>
                                                Back to customers
                                        </Button>
                                </div>
                        </FormPageLayout>
                );
        }

        if (!customerRecord) {
                return (
                        <FormPageLayout>
                                <Alert variant="destructive">
                                        <AlertTitle>Customer not found</AlertTitle>
                                        <AlertDescription>
                                                The requested customer could not be located.
                                        </AlertDescription>
                                </Alert>
                                <div className="mt-4">
                                        <Button variant="outline" onClick={() => go({ to: '/customers' })}>
                                                Back to customers
                                        </Button>
                                </div>
                        </FormPageLayout>
                );
        }

        const displayName = customerRecord ? formatDisplayName(customerRecord) : 'Customer';

        return (
                <FormPageLayout>
                        {lookupsError ? (
                                <Alert variant="destructive">
                                        <AlertTitle>Lookup data unavailable</AlertTitle>
                                        <AlertDescription>
                                                {lookupsError.message ??
                                                        'Required lookup data could not be loaded.'}
                                        </AlertDescription>
                                </Alert>
                        ) : null}

                        <CustomerForm
                                mode="edit"
                                form={form}
                                customerCategoryOptions={customerCategoryOptions}
                                contactTypeOptions={contactTypeOptions}
                                addressTypeOptions={addressTypeOptions}
                                vehicleTypeOptions={vehicleTypeOptions}
                                isSubmitting={isSubmitting}
                                isLoadingLookups={lookupsLoading}
                                currentCustomerId={customerRecord.customerId}
                                onSubmit={handleSubmit}
                                headerTitle={customerRecord ? `Edit ${displayName}` : 'Edit customer'}
                        />
                </FormPageLayout>
        );
};
